import { SubmitExamPaperErrorCode, useSubmitExam } from '@exam-room/graphql';
import { useDisclosure } from '@shared/hooks';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Alert, Button } from '../../components';
import { useExamCardExpired, useNetwork, useNewExam, useNewExamForm } from '../../providers';
import { PATHS } from '../../routes';
import { ExamSubmissionConfirmationModal } from '../../routes/exam/components';

export default function ExamReviewFooter() {
  const { examCardId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExamSubmissionConfirmationOpen, { toggle }] = useDisclosure(false);
  const { getValues } = useNewExamForm();
  const [submitExam, { loading: isSubmitting }] = useSubmitExam();
  const { showExamCardExpiredModal } = useExamCardExpired();

  const { isOnline } = useNetwork();

  const [searchParams, setSearchParams] = useSearchParams({});

  const handleBackToExam = () => {
    const q = searchParams.get('q') ?? '';
    const params = new URLSearchParams();
    params.set('q', q);
    params.set('page', 'exam');

    const query = params.toString();
    setSearchParams(query);
  };

  const submitExamPaper = async () => {
    const submitExamInput = Object.entries(getValues()).map(([, value]) => ({
      examPaperItemId: value.examPaperItemId,
      answer: { value: value.answer ?? null },
    }));

    await submitExam({
      variables: {
        submitExamInput: {
          rawAnswers: submitExamInput,
        },
      },
      onCompleted: (data) => {
        if (data.erSubmitExamPaper.isSuccess && data.erSubmitExamPaper.examPaper) {
          toggle();
          navigate(generatePath(PATHS.Exam.Submitted, { examCardId }));
        } else if (data.erSubmitExamPaper.errorCode === SubmitExamPaperErrorCode.ExamCardExpired) {
          toggle();
          showExamCardExpiredModal();
        } else {
          alert('ไม่สามารถส่งคำตอบได้ กรุณาลองใหม่อีกครั้ง');
        }
      },
      onError: () => {
        // TODO: Handle error properly and i18n
        alert('ไม่สามารถส่งคำตอบได้ กรุณาลองใหม่อีกครั้ง');
      },
    });
  };

  const { examTime } = useNewExam();
  const [canSubmit, setCanSubmit] = useState(false);

  const THIRTY_MINUTES = 30 * 60;
  const FIVE_MINUTES = 5 * 60;

  useEffect(() => {
    if (!examTime?.startedAt || !examTime?.endedAt) return;
    const startedAt = dayjs(examTime.startedAt);
    const endedAt = dayjs(examTime.endedAt);

    const interval = setInterval(() => {
      const now = dayjs();
      // Disable submit button if the time passed is less than 30 minutes or the remaining time is less than 5 minutes
      setCanSubmit(
        now.diff(startedAt, 'second') >= THIRTY_MINUTES ||
          endedAt.diff(now, 'second') <= FIVE_MINUTES,
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [examTime]);

  return (
    <div className='mt-1 flex flex-none flex-col items-center gap-6'>
      {/* TODO: SET eExam - this should be 472px to match the others */}
      <Alert
        className='w-[478px] text-start'
        variant='warning'
        message={t('examReviewFooter.alert')}
      />
      <div className='flex justify-center gap-4'>
        <Button variant='outlined' className='min-w-[232px]' onClick={handleBackToExam}>
          {t('examReviewFooter.backToExam')}
        </Button>
        <Button className='min-w-[232px]' onClick={toggle} disabled={!canSubmit || !isOnline}>
          {t('examReviewFooter.button')}
        </Button>
      </div>
      <ExamSubmissionConfirmationModal
        open={isExamSubmissionConfirmationOpen}
        onClose={toggle}
        onSubmitExam={submitExamPaper}
        submitting={isSubmitting}
      />
    </div>
  );
}
